import { BackendUser, BackendPartner } from 'common/types'

export const useProfilePercentage = () => {
  const getProfilePercentage = (user: BackendUser, partner?: BackendPartner) => {
    const commonQuestionsToFilter = [
      'Show Goals',
      'Show Country',
      'Show Province',
      'Show City',
    ]

    const partnerQuestionsToFilter = [
      'Our organization is a non-profit organization serving the needs of the Black community',
      'Email',
      'First Name',
      'Last Name',
      'Job Title',
      "We'd like to receive more information about future BPTN partnership opportunities",
      'Area of Expertise',
      `I have received official authorization from ${partner?.name} to create and manage their Obsidi Recruit account`,
      `I have received official authorization from ${partner?.name} to create and manage their Obsidi Community account`,
    ]

    const calculatePercentage = (answeredQuestions, filteredQuestions, userImage = 0) => {
      const answersToFilteredQuestions = answeredQuestions.filter((x) => filteredQuestions.some((y) => y.question === x.question && y.answer))
      
      if (answersToFilteredQuestions.length > 0) {
        const goalsQuestion = answersToFilteredQuestions.find((x) => x.question === 'What Do You Want To Accomplish at Obsidi?')
        const hasDeselectedGoals = goalsQuestion &&
          JSON.parse(goalsQuestion.answer).every((q) => q.inAnswer === 'no')

        return ((answersToFilteredQuestions.length + userImage - (hasDeselectedGoals ? 1 : 0)) / filteredQuestions.length) * 100
      }
      return 0
    }

    if (partner) {
      if (!partner.preferences) {
        return 0
      }

      const filteredQuestions = partner.preferences.filter((x) =>
        !partnerQuestionsToFilter.includes(x.question) &&
        !commonQuestionsToFilter.includes(x.question)
      );
      const answeredQuestions = filteredQuestions.filter((x) => x.answer)

      return calculatePercentage(answeredQuestions, filteredQuestions)
    }

    if (user) {
      if (!user.onboardingQuestions) {
        return 0
      }
      const filteredQuestions = user.onboardingQuestions.filter((x) =>
        !commonQuestionsToFilter.includes(x.question) &&
        x.question !== 'Do You Identify As Being Part Of The Black Community' &&
        x.question !== 'Show Resume' &&
        x.question !== 'Resume Filename'
      )

      const answeredQuestions = user.onboardingQuestions.filter((x) =>
        !commonQuestionsToFilter.includes(x.question) &&
        x.question !== 'avatar'
      ).filter((x) => x.answer)

      const userImage = user.image ? 1 : 0

      return calculatePercentage(answeredQuestions, filteredQuestions, userImage)
    }
  }
  
  const getBioCompletionPercentage = (user: BackendUser) => {
    if (!user.onboardingQuestions) {
      return 0
    }

    const bioQuestions = []
    const answeredBioQuestions = []

    const excludedQuestions = [
      'Show Goals', 
      'Show Country', 
      'Show Province', 
      'Show City',
      'Do You Identify As Being Part Of The Black Community', 
      'Show Resume',
      'resume filename', 
      'Headline', 
      'Company Name', 
      'Experience Level',
      'Industry', 
      'Area of Expertise', 
      'resume',
      'What Do You Want To Accomplish at Obsidi?', 
      'Website', 
      'Years of experience'
    ]

    user.onboardingQuestions.forEach(question => {
      if (!excludedQuestions.includes(question.question)) {
        bioQuestions.push(question)
      }

      if (question.answer && !excludedQuestions.includes(question.question)) {
        answeredBioQuestions.push(question)
      }
    })

    // filter out 'avatar' from answeredBioQuestions
    const filteredAnsweredBioQuestions = answeredBioQuestions.filter(question => question.question !== 'avatar')

    // get avatar answer from user.image
    let userImage = 0
    if (user.image) userImage = 1

    return ((filteredAnsweredBioQuestions.length + userImage) / bioQuestions.length) * 100
}
  

  const getCareerCompletionPercentage = (user: BackendUser) => {
    if (!user.onboardingQuestions) {
      return 0
    }
  
    const excludedQuestions = [
      'First Name',
      'Last Name',
      'Gender Identity',
      'Age Range',
      'Location',
      'Show Goals',
      'Show Country',
      'Show Province',
      'Show City',
      'Do You Identify As Being Part Of The Black Community',
      'Show Resume',
      'resume filename',
      'avatar',
      'resume',
      'What Do You Want To Accomplish at Obsidi?',
      'Seek Capital/Investment',
      'Build my Professional Network',
      'Source New Business Clients',
      'Find a Job',
      'Hire Talent',
      'Find Tech Solutions',
      'Belonging, Support or Community',
      'Professional Development',
      'Seeking a Mentor',
      'Obsidi Academy',
    ]
  
    const careerQuestions = user.onboardingQuestions.filter(
      (x) => !excludedQuestions.includes(x.question)
    )
  
    const answeredCareerQuestions = careerQuestions.filter((x) => x.answer)
  
    return (answeredCareerQuestions.length / careerQuestions.length) * 100
  }
  

  const resumeCompletion = (user: BackendUser) => {
    if (!user.onboardingQuestions) {
      return 0
    }
    const resumeQuestionAnswer = user.onboardingQuestions?.find(x => x.question === 'resume')
    return resumeQuestionAnswer?.answer ? 100 : 0
  }

  const goalsCompletion = (user: BackendUser) => {
    if (!user.onboardingQuestions) {
      return 0
    }
    const goalsQuestionAnswer = user.onboardingQuestions?.find(
      x => x.question === 'What Do You Want To Accomplish at Obsidi?'
    )
    let count = 0
    if (goalsQuestionAnswer && goalsQuestionAnswer.answer?.length > 0) {
      const goalsArray = JSON.parse(goalsQuestionAnswer.answer)
      goalsArray.forEach(q => {
        if (q.inAnswer === 'no') {
          count = count + 1
        }
      })
      if (count === goalsArray.length) return 0
      return 100
    }
    return 0
  }

  const getCompanyBioCompletionPercentage = (partner: BackendPartner) => {
    if (!partner?.preferences) {
      return 0;
    }
  
    const excludedQuestions = [
      'Show Goals',
      'Show Country',
      'Show Province',
      'Show City',
      'Area of Expertise',
      'Industry',
      'What Do You Want To Accomplish at Obsidi?',
      'Company Size Classification',
      'Company Website',
      `I have received official authorization from ${partner.name} to create and manage their Obsidi Recruit account`,
      `I have received official authorization from ${partner.name} to create and manage their Obsidi Community account`,
      "We'd like to receive more information about future BPTN partnership opportunities",
      "Our organization is a non-profit organization serving the needs of the Black community"
    ]
  
    const bioQuestions = partner?.preferences.filter(x => !excludedQuestions.includes(x.question))
    const answeredBioQuestions = bioQuestions.filter(x => x.answer)
  
    return (answeredBioQuestions.length / bioQuestions.length) * 100
  }  

  const getCompanySummaryCompletionPercentage = (partner: BackendPartner) => {
    if (!partner?.preferences) {
      return 0
    }
  
    const excludedQuestions = [
      'Show Goals',
      'Show Country',
      'Show Province',
      'Show City',
      'Logo',
      'Banner',
      'What Do You Want To Accomplish at Obsidi?',
      'Company Name',
      'Company Email Address',
      'Company Phone Number',
      'Bio Summary',
      'Location',
      "We'd like to receive more information about future BPTN partnership opportunities",
      "Our organization is a non-profit organization serving the needs of the Black community",
      'Area of Expertise',
      `I have received official authorization from ${partner.name} to create and manage their Obsidi Recruit account`,
      `I have received official authorization from ${partner.name} to create and manage their Obsidi Community account`
    ];
  
    const summaryQuestions = partner?.preferences.filter(x => !excludedQuestions.includes(x.question))
    const answeredSummaryQuestions = summaryQuestions.filter(x => x.answer)
  
    return (answeredSummaryQuestions.length / summaryQuestions.length) * 100
  }  

  const companyGoalsCompletion = (partner: BackendPartner) => {
    if (!partner?.preferences) {
      return 0
    }
    const goalsQuestionAnswer = partner.preferences?.find(
      x => x.question === 'What Do You Want To Accomplish at Obsidi?'
    )
    let count = 0
    if (goalsQuestionAnswer && goalsQuestionAnswer.answer?.length > 0) {
      const goalsArray = JSON.parse(goalsQuestionAnswer.answer)
      goalsArray.forEach(q => {
        if (q.inAnswer === 'no') {
          count = count + 1
        }
      })
      if (count === goalsArray.length) return 0
      return 100
    }
    return 0
  }

  const willContainerDisappear = (section: string, user, isContainerVisible) => {
    switch (section) {
      case 'bio':
        return getBioCompletionPercentage(user) == 100 ? isContainerVisible : true
      case 'career':
        return getCareerCompletionPercentage(user) == 100 ? isContainerVisible : true
      case 'resume':
        return resumeCompletion(user) == 100 ? isContainerVisible : true
      case 'goals':
        return goalsCompletion(user) == 100 ? isContainerVisible : true
    }
  }

  return {
    getProfilePercentage,
    getBioCompletionPercentage,
    getCareerCompletionPercentage,
    resumeCompletion,
    goalsCompletion,
    getCompanyBioCompletionPercentage,
    getCompanySummaryCompletionPercentage,
    companyGoalsCompletion,
    willContainerDisappear
  } as const
}
